import { gql } from '@apollo/client';
import { USER_ENROLMENTS, USER_PROFILES, USER_SUMMARY } from '../fragments';

/**
 * The only operation that issues a token, and it returns one for the account
 * whose credentials were presented. The old `loginUser` returned the token as
 * a field on User, which meant every other query selecting `token` minted one
 * too — including one an anonymous caller could run against any account.
 */
export const LOGIN_USER = gql`
  mutation loginUser($userName: String!, $password: String!) {
    loginUser(input: { userName: $userName, password: $password }) {
      token
      user {
        ...UserSummary
        ...UserProfiles
      }
    }
  }
  ${USER_SUMMARY}
  ${USER_PROFILES}
`;

export const REGISTER_TEACHER = gql`
  mutation registerTeacher($input: RegisterUserInput!) {
    registerUser(input: $input) {
      ...UserSummary
      ...UserEnrolments
    }
  }
  ${USER_SUMMARY}
  ${USER_ENROLMENTS}
`;

/** The admission number is allocated server-side, from a counter. */
export const REGISTER_STUDENT = gql`
  mutation registerStudent($input: RegisterUserInput!) {
    registerUser(input: $input) {
      ...UserSummary
      profileStudent {
        admissionNumber
      }
    }
  }
  ${USER_SUMMARY}
`;

/**
 * The caller's own details, and only the fields they own. The old mutation
 * took Prisma's generated update input, which accepted every column — role
 * included — on whichever row the `where` named.
 */
export const UPDATE_USER_INFO = gql`
  mutation updateUserInfo($input: UpdateUserInput!) {
    updateUser(input: $input) {
      ...UserSummary
      ...UserEnrolments
      ...UserProfiles
    }
  }
  ${USER_SUMMARY}
  ${USER_ENROLMENTS}
  ${USER_PROFILES}
`;

/**
 * Editing somebody else's account. Separate from `updateUser` — which is
 * scoped to the caller — so widening what a user may change about themselves
 * stays a separate decision from what an administrator may change about
 * anyone. `role` and `status` exist only here.
 */
export const ADMIN_UPDATE_USER = gql`
  mutation adminUpdateUser($userId: String!, $input: AdminUpdateUserInput!) {
    adminUpdateUser(userId: $userId, input: $input) {
      ...UserSummary
      ...UserEnrolments
      ...UserProfiles
    }
  }
  ${USER_SUMMARY}
  ${USER_ENROLMENTS}
  ${USER_PROFILES}
`;

/** A cohort and the subjects taken in it, together — see enrolment guards. */
export const ENROL_USER = gql`
  mutation enrolUser($userId: String!, $cohortId: String!, $subjectIds: [String!]!) {
    enrolUser(userId: $userId, cohortId: $cohortId, subjectIds: $subjectIds) {
      ...UserSummary
      ...UserEnrolments
    }
  }
  ${USER_SUMMARY}
  ${USER_ENROLMENTS}
`;

/** Withdrawal takes the cohort's subjects with it. */
export const WITHDRAW_FROM_COHORT = gql`
  mutation withdrawFromCohort($userId: String!, $cohortId: String!) {
    withdrawFromCohort(userId: $userId, cohortId: $cohortId) {
      ...UserSummary
      ...UserEnrolments
    }
  }
  ${USER_SUMMARY}
  ${USER_ENROLMENTS}
`;

export const DELETE_USER = gql`
  mutation deleteUser($userId: String!) {
    deleteUser(userId: $userId)
  }
`;

export const INIT_FORGET_PASSWORD = gql`
  mutation initForgetPassword($email: String!) {
    initForgetPassword(email: $email)
  }
`;

/**
 * No email argument: the reset token names its own subject. The old mutation
 * took one, and the token it checked was signed for whoever the caller said.
 */
export const COMPLETE_FORGET_PASSWORD = gql`
  mutation completeForgetPassword($input: CompleteForgetPasswordInput!) {
    completeForgetPassword(input: $input)
  }
`;

export const RESET_PASSWORD = gql`
  mutation resetPassword($userId: String!) {
    resetPassword(userId: $userId)
  }
`;

/** The account changed is the one holding the token, not one named in input. */
export const CHANGE_PASSWORD = gql`
  mutation changePassword($input: ChangePasswordInput!) {
    changePassword(input: $input)
  }
`;
